import {
  calculateDonationCharge,
  DEFAULT_PROCESSING_RATE,
} from '../lib/payments/constants.ts'
import {
  NO_TIPS_STATEMENT,
  PLATFORM_FEE_STATEMENT,
  PROCESSING_FEE_DISCLOSURE,
} from './pricing-content.ts'
import type { BlogAuthorId } from './blog-authors.ts'

export type ComparisonSource = {
  publisher: 'KindLumen' | 'Zeffy' | 'Stripe'
  title: string
  url: string
  checkedOn: string
  supports: string
}

export const comparisonSources = {
  kindlumenPricing: {
    publisher: 'KindLumen',
    title: 'KindLumen pricing',
    url: 'https://www.kindlumen.com/pricing',
    checkedOn: '2026-09-03',
    supports: 'Platform pricing, optional transaction-cost coverage, the 1% contribution, and payout language.',
  },
  kindlumenFeatures: {
    publisher: 'KindLumen',
    title: 'KindLumen features',
    url: 'https://www.kindlumen.com/features',
    checkedOn: '2026-09-03',
    supports: 'Donation forms, recurring giving, campaign pages, receipts, records, and own-Stripe positioning.',
  },
  kindlumenIntegrations: {
    publisher: 'KindLumen',
    title: 'KindLumen integrations',
    url: 'https://www.kindlumen.com/integrations',
    checkedOn: '2026-09-03',
    supports: 'Inline embeds, popup buttons, hosted pages, and supported website builders.',
  },
  kindlumenWordPress: {
    publisher: 'KindLumen',
    title: 'KindLumen WordPress plugin',
    url: 'https://www.kindlumen.com/wordpress-plugin',
    checkedOn: '2026-09-03',
    supports: 'WordPress plugin, shortcodes, block, popup button, full form, and campaign cards.',
  },
  kindlumenTerms: {
    publisher: 'KindLumen',
    title: 'KindLumen terms of use',
    url: 'https://www.kindlumen.com/terms',
    checkedOn: '2026-09-03',
    supports: 'Processor responsibility, refunds, disputes, and service boundaries.',
  },
  zeffyOverview: {
    publisher: 'Zeffy',
    title: 'Zeffy all-in-one fundraising platform',
    url: 'https://www.zeffy.com/',
    checkedOn: '2026-09-03',
    supports: 'Donation forms, events, raffles, auctions, store, peer-to-peer, memberships, CRM, and communications.',
  },
  zeffyPricing: {
    publisher: 'Zeffy',
    title: 'How Zeffy is 100% free',
    url: 'https://www.zeffy.com/home/free-online-fundraising-platform',
    checkedOn: '2026-09-03',
    supports: 'No platform, transaction, or credit-card fees for nonprofits and Zeffy’s optional-contribution model.',
  },
  zeffyContribution: {
    publisher: 'Zeffy',
    title: 'Zeffy really is free: no fees, no catch',
    url: 'https://support.zeffy.com/zeffy-really-is-free-no-fees-no-catch-4zjir',
    checkedOn: '2026-09-03',
    supports: 'Optional donor contributions, the ability to select $0, and how Zeffy covers processing fees.',
  },
  zeffyEmbed: {
    publisher: 'Zeffy',
    title: 'Sharing your Zeffy campaign',
    url: 'https://support.zeffy.com/sharing-your-zeffy-campaign-with-your-supporters-and-community-k5z1c',
    checkedOn: '2026-09-03',
    supports: 'On-page campaign embeds and which campaign context is omitted from embedded forms.',
  },
  zeffyWordPress: {
    publisher: 'Zeffy',
    title: 'Using the Zeffy WordPress Donate Button plugin',
    url: 'https://support.zeffy.com/using-the-zeffy-wordpress-donate-button-plugin-714z1',
    checkedOn: '2026-09-03',
    supports: 'WordPress plugin, embedded forms, and popup donation buttons.',
  },
  zeffyPayouts: {
    publisher: 'Zeffy',
    title: 'Zeffy payouts: schedules, amounts, and reports',
    url: 'https://support.zeffy.com/zeffy-payouts-schedules-amounts-and-reports-ajuec',
    checkedOn: '2026-09-03',
    supports: 'Weekly and monthly payout schedules, next-day eligibility, and the processing-review period.',
  },
  zeffyEligibility: {
    publisher: 'Zeffy',
    title: 'Is my organization eligible to use Zeffy?',
    url: 'https://support.zeffy.com/is-my-organization-eligible-to-use-zeffy-8m24r',
    checkedOn: '2026-09-03',
    supports: 'Supported countries, nonprofit eligibility, and the organizational bank-account requirement.',
  },
  stripePricing: {
    publisher: 'Stripe',
    title: 'Stripe pricing',
    url: 'https://stripe.com/pricing',
    checkedOn: '2026-09-03',
    supports: 'The US standard domestic-card rate used in the worked examples.',
  },
  stripeNonprofit: {
    publisher: 'Stripe',
    title: 'Stripe fee discount for nonprofit organizations',
    url: 'https://support.stripe.com/questions/fee-discount-for-nonprofit-organizations',
    checkedOn: '2026-09-03',
    supports: 'Eligibility and application requirements for Stripe’s nonprofit discount.',
  },
} as const satisfies Record<string, ComparisonSource>

export type ComparisonSourceId = keyof typeof comparisonSources

export type SourceBackedItem = {
  sourceIds: readonly ComparisonSourceId[]
}

export type ComparisonTableRow = SourceBackedItem & {
  criterion: string
  question: string
  kindlumen: string
  zeffy: string
  takeaway: string
}

export type ComparisonAnalysisSection = SourceBackedItem & {
  id: string
  heading: string
  verdict: string
  paragraphs: readonly string[]
}

export type ComparisonDecision = {
  heading: string
  summary: string
  reasons: readonly string[]
}

export type ComparisonFaq = {
  question: string
  answer: string
}

export type FeeOutcome = {
  provider: 'KindLumen' | 'Zeffy'
  donorTotalCents: number
  organizationReceivesCents: number
  processingAndPlatformCostCents: number
  note: string
}

export type WorkedFeeExample = SourceBackedItem & {
  id: string
  title: string
  gifts: number
  intendedGiftCents: number
  assumptions: readonly string[]
  outcomes: readonly FeeOutcome[]
  finding: string
}

export type ComparisonPage = {
  authorId: BlogAuthorId
  slug: string
  path: string
  eyebrow: string
  title: string
  seoTitle: string
  description: string
  keywords: readonly string[]
  publishedAt: string
  verifiedAt: string
  verifiedLabel: string
  readTime: string
  competitor: string
  competitorUrl: string
  disclosure: string
  quickAnswer: string
  summaryPoints: readonly string[]
  bestFor: readonly ComparisonDecision[]
  table: readonly ComparisonTableRow[]
  feeExamples: readonly WorkedFeeExample[]
  feeCoverageExample: SourceBackedItem & {
    title: string
    intro: string
    donorPaysCents: number
    organizationReceivesCents: number
    stripeProcessingCents: number
    kindlumenContributionCents: number
    caveat: string
  }
  analysis: readonly ComparisonAnalysisSection[]
  competitorAdvantages: readonly string[]
  kindlumenLimitations: readonly string[]
  checklist: readonly string[]
  methodology: readonly string[]
  sourceIds: readonly ComparisonSourceId[]
  correctionEmail: string
  faqs: readonly ComparisonFaq[]
}

export const comparisonHub = {
  path: '/compare',
  title: 'Donation Platform Comparisons | KindLumen',
  description:
    'Evidence-led donation platform comparisons with documented sources, worked fee examples, explicit assumptions, and honest tradeoffs for nonprofit teams.',
  keywords: [
    'donation platform comparisons',
    'nonprofit fundraising software comparison',
    'free donation platform comparison',
    'fundraising platform fees',
  ],
  standards: [
    {
      title: 'Start with first-party evidence',
      description: 'Product and support documentation outrank review roundups, affiliate pages, and screenshots without context.',
    },
    {
      title: 'Hold the assumptions still',
      description: 'Fee examples use the same gift size, transaction count, currency, payment method, and donor choice.',
    },
    {
      title: 'Name both sides of the tradeoff',
      description: 'Every review includes the competitor’s advantages and the current limits of KindLumen.',
    },
    {
      title: 'Date the evidence',
      description: 'Each comparison has a last-verified date, source list, methodology, and a public correction path.',
    },
  ],
  questions: [
    'Do you mainly need a donation form, or do you also run ticketed events, auctions, raffles, memberships, and peer-to-peer campaigns?',
    'Must the organization receive the full intended gift when a donor declines every optional extra?',
    'Do you want donations managed in your own Stripe account, or within an all-in-one fundraising platform?',
    'Should donors stay on your website, and how much campaign context must remain visible inside the embed?',
    'What payout schedule, country eligibility, and bank-account requirements must the platform meet?',
    'Will your team use a built-in CRM and communications suite, or keep those functions in separate systems?',
  ],
} as const

const standardSingleGift = calculateDonationCharge({
  intendedAmountCents: 100_00,
  donorCoversCosts: false,
  rate: DEFAULT_PROCESSING_RATE,
})

const coveredSingleGift = calculateDonationCharge({
  intendedAmountCents: 100_00,
  donorCoversCosts: true,
  rate: DEFAULT_PROCESSING_RATE,
})

const campaignGiftCount = 100
const campaignIntendedGiftCents = 100_00
const campaignTotalCents = campaignGiftCount * campaignIntendedGiftCents
const campaignProcessingCents = campaignGiftCount * standardSingleGift.estimatedProcessingFeeCents

export const kindlumenVsZeffy = {
  authorId: 'kiran-patel',
  slug: 'kindlumen-vs-zeffy',
  path: '/compare/kindlumen-vs-zeffy',
  eyebrow: 'KindLumen vs. Zeffy',
  title: 'KindLumen vs. Zeffy: a fair, source-backed comparison',
  seoTitle: 'KindLumen vs. Zeffy: Fees, Features & Fit (2026)',
  description:
    'Compare KindLumen and Zeffy on fees, donor checkout, website embeds, fundraising tools, CRM depth, payouts, and eligibility—with sourced claims and worked examples.',
  keywords: [
    'KindLumen vs Zeffy',
    'Zeffy alternative',
    'Zeffy fees',
    'free fundraising platform',
    'Stripe donation form comparison',
  ],
  publishedAt: '2026-09-03',
  verifiedAt: '2026-09-03',
  verifiedLabel: 'September 3, 2026',
  readTime: '12-minute read',
  competitor: 'Zeffy',
  competitorUrl: 'https://www.zeffy.com/',
  disclosure:
    'KindLumen publishes this comparison and therefore has a commercial interest in the outcome. Zeffy did not review or sponsor it, and KindLumen is not affiliated with Zeffy. We use first-party documentation, show our assumptions, and state where Zeffy has the stronger offer.',
  quickAnswer:
    'Zeffy is the stronger all-in-one choice when eliminating organization-paid processing fees and running events, raffles, auctions, memberships, peer-to-peer campaigns, and donor communications in one platform matter most. KindLumen is the more focused choice when you want a branded donation layer on the website you already have, with gifts managed through your own Stripe account. The tradeoff is real: uncovered Stripe processing fees still apply with KindLumen, while Zeffy covers them.',
  summaryPoints: [
    'Zeffy wins the pure fee comparison: it says the nonprofit keeps 100% even when a donor contributes $0 to Zeffy.',
    'KindLumen’s clearest advantage is operational fit for an existing Stripe-and-website stack, not a claim that it has more fundraising modules.',
    'Both support recurring gifts, on-page embeds, hosted links, and WordPress; neither should be described as hosted-only.',
  ],
  bestFor: [
    {
      heading: 'Choose Zeffy when breadth and net revenue come first',
      summary:
        'Zeffy is unusually difficult to beat on organization cost, and its product scope extends far beyond donation forms.',
      reasons: [
        'The organization pays no platform, transaction, or credit-card fee on Zeffy.',
        'Events, ticketing, raffles, auctions, a store, peer-to-peer campaigns, memberships, donor CRM, and communications are included.',
        'Your team prefers one fundraising system over a focused donation layer plus separate operations tools.',
      ],
    },
    {
      heading: 'Choose KindLumen when your own Stripe stack comes first',
      summary:
        'KindLumen is deliberately narrower: a website-first donation experience connected to the organization’s own Stripe account.',
      reasons: [
        'You already use Stripe and want donations, subscriptions, and payout timing anchored there.',
        'You need an inline form, popup, hosted page, or WordPress path without rebuilding the rest of your website.',
        'A focused donation workflow is preferable to adopting a broader events-and-CRM platform.',
      ],
    },
  ],
  table: [
    {
      criterion: 'Organization cost',
      question: 'What is deducted when the donor adds no optional extra?',
      kindlumen: `${PLATFORM_FEE_STATEMENT} ${PROCESSING_FEE_DISCLOSURE}`,
      zeffy: 'Zeffy says the nonprofit pays no platform, transaction, or credit-card fees and keeps 100% of the intended gift.',
      takeaway: 'Zeffy has the stronger fee outcome when donors decline optional extras.',
      sourceIds: ['kindlumenPricing', 'zeffyPricing', 'zeffyContribution'],
    },
    {
      criterion: 'Donor-funded model',
      question: 'What optional amount may donors see?',
      kindlumen: `A donor may cover processing costs; that extra includes a 1% KindLumen contribution. ${NO_TIPS_STATEMENT}`,
      zeffy: 'A donor may add a contribution to Zeffy, adjust it, or set it to $0. Zeffy does not publish one fixed contribution percentage to use in a fair model.',
      takeaway: 'Both models use an optional donor-paid amount, but the labels and mechanics differ.',
      sourceIds: ['kindlumenPricing', 'zeffyContribution'],
    },
    {
      criterion: 'Donation essentials',
      question: 'Can each product handle everyday online giving?',
      kindlumen: 'Branded one-time and recurring forms, suggested and custom amounts, automatic receipts, donor records, and campaign pages.',
      zeffy: 'Branded one-time and recurring forms, automatic tax receipts, campaign pages, and donor records within its wider platform.',
      takeaway: 'Both cover the core donation-form job.',
      sourceIds: ['kindlumenFeatures', 'zeffyOverview'],
    },
    {
      criterion: 'Fundraising breadth',
      question: 'What can the team run beyond donation forms?',
      kindlumen: 'Campaigns are included; native ticketing, auctions, raffles, a store, memberships, and peer-to-peer fundraising are not part of the current documented feature set.',
      zeffy: 'Events and ticketing, raffles, auctions, a store, memberships, and peer-to-peer fundraising are included.',
      takeaway: 'Zeffy is substantially broader.',
      sourceIds: ['kindlumenFeatures', 'zeffyOverview'],
    },
    {
      criterion: 'Website experience',
      question: 'Can donors complete a gift without leaving the nonprofit site?',
      kindlumen: 'Yes. KindLumen documents inline forms and popup buttons, plus a hosted link when an embed is not practical.',
      zeffy: 'Yes. Zeffy documents an on-page embed. Its support page says the embed shows transaction fields but omits campaign description, images, logo, and nonprofit name.',
      takeaway: 'Both embed; compare the context and branding each embedded state preserves on your actual site.',
      sourceIds: ['kindlumenIntegrations', 'zeffyEmbed'],
    },
    {
      criterion: 'WordPress',
      question: 'Is there a first-party WordPress route?',
      kindlumen: 'Yes. KindLumen documents a plugin with a block, shortcodes, full-form and popup options, and campaign cards.',
      zeffy: 'Yes. Zeffy documents a plugin that embeds a campaign or opens it from a popup donation button.',
      takeaway: 'Both have a credible WordPress path; test the exact layout you plan to ship.',
      sourceIds: ['kindlumenWordPress', 'zeffyWordPress'],
    },
    {
      criterion: 'Funds and payouts',
      question: 'Where is payout timing managed?',
      kindlumen: 'Donations are paid into the organization’s own Stripe account; Stripe then pays the bank on that account’s normal payout timing.',
      zeffy: 'Zeffy sends direct deposits on a weekly default or monthly schedule; next-day payout is available to eligible organizations after payment processing and review.',
      takeaway: 'KindLumen fits teams that want Stripe to remain the payment system of record; Zeffy manages its own payout workflow.',
      sourceIds: ['kindlumenPricing', 'zeffyPayouts'],
    },
    {
      criterion: 'CRM and communications',
      question: 'How much supporter management is included?',
      kindlumen: 'Donor records, receipts, and donation reporting support the donation workflow; a full marketing CRM is not part of the documented product scope.',
      zeffy: 'A donor CRM, segmentation, email campaigns, newsletters, and direct-mail tools are part of the all-in-one platform.',
      takeaway: 'Zeffy is stronger if your team wants fundraising and supporter communications in one system.',
      sourceIds: ['kindlumenFeatures', 'zeffyOverview'],
    },
    {
      criterion: 'Eligibility',
      question: 'Who can open an account?',
      kindlumen: 'The public KindLumen product pages reviewed for this comparison do not publish a country-by-country eligibility matrix. Confirm availability before planning a migration.',
      zeffy: 'Zeffy currently lists nonprofits in the US, Canada, UK, Ireland, Australia, and Germany, with an organizational bank account and country-specific requirements.',
      takeaway: 'Zeffy publishes clearer eligibility boundaries; either way, verify before switching.',
      sourceIds: ['kindlumenTerms', 'zeffyEligibility'],
    },
  ],
  feeExamples: [
    {
      id: 'single-gift',
      title: 'One $100 domestic card gift',
      gifts: 1,
      intendedGiftCents: 100_00,
      assumptions: [
        'US dollars and one successful domestic online card payment.',
        'Stripe’s published standard rate of 2.9% + 30¢ is used for KindLumen.',
        'The donor chooses no optional transaction-cost coverage or platform contribution on either checkout.',
        'Refunds, disputes, international cards, currency conversion, ACH, and custom pricing are excluded.',
      ],
      outcomes: [
        {
          provider: 'KindLumen',
          donorTotalCents: standardSingleGift.grossChargeAmountCents,
          organizationReceivesCents: standardSingleGift.estimatedOrganizationNetCents,
          processingAndPlatformCostCents: standardSingleGift.estimatedProcessingFeeCents,
          note: 'KindLumen takes $0; the estimated $3.20 is Stripe processing.',
        },
        {
          provider: 'Zeffy',
          donorTotalCents: 100_00,
          organizationReceivesCents: 100_00,
          processingAndPlatformCostCents: 0,
          note: 'Zeffy says it covers processing even when the donor contributes $0.',
        },
      ],
      finding: 'Under these assumptions, Zeffy leaves the organization $3.20 more from the $100 gift.',
      sourceIds: ['kindlumenPricing', 'stripePricing', 'zeffyContribution'],
    },
    {
      id: 'campaign',
      title: 'A $10,000 campaign made up of 100 $100 gifts',
      gifts: campaignGiftCount,
      intendedGiftCents: campaignIntendedGiftCents,
      assumptions: [
        'US dollars and 100 successful domestic online card payments of $100 each.',
        'Stripe’s published standard rate of 2.9% + 30¢ is used for KindLumen.',
        'No donor chooses an optional extra; this is a controlled scenario, not a forecast of donor behavior.',
        'The example excludes refunds, disputes, international cards, currency conversion, ACH, and negotiated rates.',
      ],
      outcomes: [
        {
          provider: 'KindLumen',
          donorTotalCents: campaignTotalCents,
          organizationReceivesCents: campaignTotalCents - campaignProcessingCents,
          processingAndPlatformCostCents: campaignProcessingCents,
          note: 'The per-transaction 30¢ matters: 100 separate gifts create 100 fixed charges.',
        },
        {
          provider: 'Zeffy',
          donorTotalCents: campaignTotalCents,
          organizationReceivesCents: campaignTotalCents,
          processingAndPlatformCostCents: 0,
          note: 'The organization keeps the intended $10,000; no donor contribution is assumed.',
        },
      ],
      finding: 'Under these assumptions, Zeffy leaves the organization $320 more across the campaign.',
      sourceIds: ['kindlumenPricing', 'stripePricing', 'zeffyContribution'],
    },
  ],
  feeCoverageExample: {
    title: 'What changes when a KindLumen donor covers costs?',
    intro:
      'For a $100 intended gift at Stripe’s standard domestic-card rate, KindLumen solves the gross charge so the organization still nets $100 after processing and the 1% KindLumen contribution.',
    donorPaysCents: coveredSingleGift.grossChargeAmountCents,
    organizationReceivesCents: coveredSingleGift.estimatedOrganizationNetCents,
    stripeProcessingCents: coveredSingleGift.estimatedProcessingFeeCents,
    kindlumenContributionCents: coveredSingleGift.kindlumenContributionCents,
    caveat:
      'This illustrates the checkout math only. It does not assume how often donors select coverage. Zeffy’s donor contribution is not assigned a percentage because its documentation says the suggestion varies with the transaction amount.',
    sourceIds: ['kindlumenPricing', 'stripePricing', 'zeffyContribution'],
  },
  analysis: [
    {
      id: 'fees',
      heading: 'Fees: Zeffy has the cleaner answer for the organization',
      verdict: 'Fee verdict: Zeffy',
      paragraphs: [
        'The honest fee comparison is not “both are free.” Both charge the organization $0 in platform fees, but KindLumen routes payments through the organization’s Stripe account. When a donor declines transaction-cost coverage, Stripe processing comes out of the gift. Zeffy says it covers those processing costs regardless of whether the donor adds a contribution.',
        'That makes Zeffy the better financial result in the controlled no-extra scenario above. KindLumen can still deliver the full intended gift when a donor elects to cover costs, but donor participation should never be assumed. Eligible nonprofits may ask Stripe for discounted pricing, although Stripe’s current support page describes eligibility and an application process rather than promising the discount automatically.',
      ],
      sourceIds: ['kindlumenPricing', 'zeffyPricing', 'zeffyContribution', 'stripePricing', 'stripeNonprofit'],
    },
    {
      id: 'scope',
      heading: 'Product scope: focused donation layer or all-in-one platform',
      verdict: 'Breadth verdict: Zeffy',
      paragraphs: [
        'KindLumen centers on online donations: branded forms, one-time and monthly giving, campaign pages, receipts, donor records, and website embeds. That smaller surface can be an advantage when the rest of the stack already works and the only missing piece is a modern giving experience.',
        'Zeffy is built for a wider operating model. Its current product pages include event ticketing, raffles, auctions, an online store, peer-to-peer fundraising, memberships, donor CRM, email, newsletters, and direct mail. A team that would otherwise buy several tools should treat that breadth as a major Zeffy advantage, not an incidental feature-list win.',
      ],
      sourceIds: ['kindlumenFeatures', 'zeffyOverview'],
    },
    {
      id: 'website',
      heading: 'Website experience: both embed, but the embedded context differs',
      verdict: 'Website verdict: test both in the real page',
      paragraphs: [
        'It would be inaccurate to frame Zeffy as a hosted-only checkout. Zeffy documents an on-page embed and a WordPress plugin, while KindLumen documents inline, popup, hosted, and WordPress options. Both can keep the transaction on the organization’s website.',
        'The more useful question is what survives inside the embed. Zeffy’s support page says its embedded form shows transaction fields but not the campaign description, images, logo, or nonprofit name. KindLumen positions the embedded form itself as branded. Teams should publish a staging page for each option and review mobile layout, organization identity, campaign context, accessibility, and checkout copy before choosing.',
      ],
      sourceIds: ['kindlumenIntegrations', 'kindlumenWordPress', 'zeffyEmbed', 'zeffyWordPress'],
    },
    {
      id: 'payouts',
      heading: 'Payouts: decide where your finance team wants control',
      verdict: 'Payout verdict: depends on the operating model',
      paragraphs: [
        'KindLumen sends donations into the organization’s own Stripe account and does not add a second platform payout schedule. That is attractive when the finance team already reconciles Stripe, wants subscriptions to remain there, or expects to use Stripe alongside other payment activity.',
        'Zeffy manages direct deposits to the organization’s bank account. Its current help center lists weekly payouts as the default, monthly payouts as an option, and next-day payouts for eligible organizations, after a mandatory processing and anti-fraud review. Neither route means “instant bank cash.” The better one is the workflow your finance team can reconcile confidently.',
      ],
      sourceIds: ['kindlumenPricing', 'zeffyPayouts'],
    },
    {
      id: 'eligibility',
      heading: 'Eligibility: verify this before you design a migration',
      verdict: 'Documentation verdict: Zeffy is clearer',
      paragraphs: [
        'Zeffy currently publishes a specific boundary: eligible nonprofit organizations in the United States, Canada, the United Kingdom, Ireland, Australia, and Germany, with a bank account held in the organization’s name and additional country-specific requirements.',
        'The KindLumen public pages reviewed for this article do not provide an equivalent country matrix. That is a KindLumen documentation gap. If location, nonprofit type, fiscal sponsorship, or bank ownership is unusual, confirm eligibility directly before exporting data or changing a live donate button.',
      ],
      sourceIds: ['kindlumenTerms', 'zeffyEligibility'],
    },
  ],
  competitorAdvantages: [
    'Zeffy covers processing fees, so the nonprofit keeps the full intended amount even when the donor contributes $0 to Zeffy.',
    'Zeffy includes substantially more fundraising formats: ticketing, raffles, auctions, a store, peer-to-peer campaigns, and memberships.',
    'Zeffy combines donor CRM, segmentation, and outbound communications with transaction data.',
    'Zeffy publishes detailed eligibility and payout documentation that makes operational review easier.',
  ],
  kindlumenLimitations: [
    'Stripe processing fees reduce the organization’s net when the donor declines optional transaction-cost coverage.',
    'KindLumen does not currently document native ticketing, auctions, raffles, a store, memberships, or peer-to-peer fundraising.',
    'KindLumen’s donor records and reporting are not positioned as a full CRM and communications suite.',
    'KindLumen does not yet publish a country-by-country eligibility matrix on the public pages reviewed here.',
  ],
  checklist: [
    'Run the last 12 months of gifts through both fee models using your real average gift and transaction count.',
    'Build the same live campaign in both products and test the full donor flow on a phone and keyboard.',
    'Confirm whether events, memberships, auctions, or peer-to-peer campaigns must live in the same system.',
    'Ask finance which payout schedule and reconciliation source it wants to own.',
    'Export a sample report and confirm the fields needed by your CRM, accounting, and receipt workflows.',
    'Verify organization eligibility, bank-account ownership, tax-receipt needs, and refund handling in writing.',
    'Read the donor-facing optional-contribution copy on the final payment screen rather than relying on marketing summaries.',
  ],
  methodology: [
    'We reviewed first-party KindLumen product, pricing, integration, WordPress, and terms pages, plus first-party Zeffy product and help-center pages. Stripe’s official pricing and nonprofit-support pages provide the processing assumptions.',
    'Claims are limited to features and policies documented on those pages as of the verification date. We do not use affiliate rankings, invented customer quotes, or private roadmap claims.',
    'Fee examples hold gift size, transaction count, currency, card type, and donor behavior constant. They use integer-cent calculations and state what is excluded.',
    'A missing public claim is described as “not documented,” not proof that a capability can never be provided. Readers should verify contract, checkout, and eligibility details before purchasing or migrating.',
    'We name the authoring conflict, publish Zeffy’s strongest advantages, list KindLumen’s current limitations, and invite corrections. Material corrections should update the verification date and affected copy together.',
  ],
  sourceIds: [
    'kindlumenPricing',
    'kindlumenFeatures',
    'kindlumenIntegrations',
    'kindlumenWordPress',
    'kindlumenTerms',
    'zeffyOverview',
    'zeffyPricing',
    'zeffyContribution',
    'zeffyEmbed',
    'zeffyWordPress',
    'zeffyPayouts',
    'zeffyEligibility',
    'stripePricing',
    'stripeNonprofit',
  ],
  correctionEmail: 'support@kindlumen.com',
  faqs: [
    {
      question: 'Is Zeffy actually free for nonprofits?',
      answer:
        'According to Zeffy’s current pricing and support pages, yes: Zeffy charges the nonprofit no platform, transaction, or credit-card fees and says the nonprofit keeps 100% of the intended gift. Zeffy funds the service through optional donor contributions.',
    },
    {
      question: 'Does Zeffy force donors to contribute?',
      answer:
        'No. Zeffy’s support documentation says the contribution is optional, can be adjusted, and can be set to $0. This comparison does not assign Zeffy a fixed suggested contribution percentage because Zeffy says the suggestion varies with the transaction amount.',
    },
    {
      question: 'Does KindLumen charge a platform fee?',
      answer:
        'No. KindLumen charges the organization no setup, subscription, or platform fee. Stripe processing is deducted when a donor declines transaction-cost coverage; when a donor elects coverage, the extra includes processing and a 1% KindLumen contribution.',
    },
    {
      question: 'Can Zeffy and KindLumen both embed on a nonprofit website?',
      answer:
        'Yes. Both document on-page embeds and WordPress paths. Zeffy says its embedded form omits campaign description, images, logo, and nonprofit name, so test whether the surrounding page supplies enough context for donors.',
    },
    {
      question: 'Which is better for events, auctions, or peer-to-peer fundraising?',
      answer:
        'Zeffy. Those tools are part of Zeffy’s documented all-in-one platform, while they are not part of KindLumen’s current documented feature set. KindLumen is focused on website donation forms, recurring giving, and campaigns.',
    },
    {
      question: 'Which is better for an organization that already uses Stripe?',
      answer:
        'KindLumen may be the more natural operational fit because donations are managed through the organization’s own Stripe account. That does not make it cheaper in every scenario: uncovered Stripe processing fees still apply, so compare workflow control against Zeffy’s zero-fee outcome.',
    },
  ],
} as const satisfies ComparisonPage

export const comparisonPages = [kindlumenVsZeffy] as const satisfies readonly ComparisonPage[]

export function getComparisonBySlug(slug: string) {
  return comparisonPages.find((comparison) => comparison.slug === slug)
}

export function getComparisonPaths() {
  return comparisonPages.map((comparison) => comparison.path)
}
